import { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useFormik } from "formik"
import * as Yup from "yup"
import { getAllCities } from "../../app/store/slices/cities"
import { sendContactUsForm, findTickets } from "../../app/store/slices/contact-us/contactUs"
import { EMAIL_REGEX, PHONE_REGEX } from "../../utils/validations"
import { errorToast, successToastTopRight } from "../../utils/toasts/toasts"

export default function useContactUs() {
    const dispatch = useDispatch()

    const { enquiryTypes } = useSelector((state) => state.contactUs)
    const { allCities } = useSelector((state) => state.cities);

    const [openGuestPopup, setOpenGuestPopup] = useState(false);
    const [loading, setLoading] = useState(false);


    /**
     * Initial values for the formik form
     */
    const initialValues = {
        enquiryType: null,
        name: '',
        contactEmail: '',
        contactNumber: '',
        companyName: '',
        location: '',
        orderId: '',
        emailIdUsedAtCheckout: '',
        eventUrl: '',
        message: '',
    }


    /**
     * Yup validation schema for the form
     */
    const validationSchema = Yup.object({
        enquiryType: Yup.object().required('Enquiry type selection is required'),
        name: Yup.string().required('Name is required'),
        contactEmail: Yup.string().matches(EMAIL_REGEX, 'Invalid email format').required('Contact email is required'),
        contactNumber: Yup.string().matches(PHONE_REGEX, 'Invalid phone number').required('Contact number is required'),
        message: Yup.string().required('Message is required').required('Message is required'),
        companyName: Yup.string()
            .when('enquiryType', {
                is: 'business_query',
                then: (schema) => schema.required('Company Name is required'),
                otherwise: (schema) => schema.notRequired()
            }),
        location: Yup.object()
            .when('enquiryType', {
                is: (value) => value?.value === 'business_query',
                then: (schema) => schema.required('Location is required'),
                otherwise: (schema) => schema.notRequired()
            }),
        orderId: Yup.string()
            .when('enquiryType', {
                is: (value) => value?.value === 'purchase_order_query',
                then: (schema) => schema.required('Order ID is required'),
                otherwise: (schema) => schema.notRequired()
            }),
        emailIdUsedAtCheckout: Yup.string()
            .when('enquiryType', {
                is: (value) => value?.value === 'purchase_order_query',
                then: (schema) => schema.required('Email ID is required').matches(EMAIL_REGEX, 'Invalid email format'),
                otherwise: (schema) => schema.notRequired()
            }),
        eventUrl: Yup.string()
            .url('Invalid Event URL format')
            .when('enquiryType', {
                is: (value) => value?.value === 'report_issue',
                then: (schema) => schema
                    .required('Event URL is required'),
                // .matches(
                //     /^https:\/\/www\.shmooz\.in\/e\/[\w-]+$/,
                //     'Invalid Event URL format'
                // ),
                otherwise: (schema) => schema.notRequired()
            })
    })


    /**
     * formik instance
     */
    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: async (values) => {
            try {
                setLoading(true);

                const payload = {
                    enquiry_type: values.enquiryType?.value,
                    name: values.name,
                    contact_email: values.contactEmail,
                    contact_number: values.contactNumber,
                    company_name: values.companyName,
                    location: values.location?.name,
                    order_id: values.orderId,
                    checkout_email: values.emailIdUsedAtCheckout,
                    event_url: values.eventUrl,
                    message: values.message,
                }

                const response = await dispatch(sendContactUsForm(payload)).unwrap();

                if (response.success && response.code === 200) {
                    successToastTopRight('Thank you for reaching out to us. We will get back to you shortly.');
                }

                formik.resetForm();
            } catch (error) {
                errorToast('Failed to send the message. Please try again later.');
            } finally {
                setLoading(false);
            }
        }
    })


    /**
     * Fetch all cities if not already fetched
     */
    useEffect(() => {
        if (!allCities?.length || !allCities) {
            dispatch(getAllCities());
        }
    }, [dispatch])


    /**
     * Find ticket form initial values
     */
    const findTicketInitialValues = {
        userName: '',
        email: '',
        phone: '',
        eventName: '',
        orderId: ''
    }


    /**
     * Yup validation schema for the find ticket form
     */
    const findTicketValidationSchema = Yup.object({
        userName: Yup.string().required('Name is required'),
        email: Yup.string().matches(EMAIL_REGEX, 'Invalid email format').required('Email is required'),
        phone: Yup.string().matches(PHONE_REGEX, 'Invalid phone number').required('Phone number is required'),
        eventName: Yup.string().required('Event name is required'),
        orderId: Yup.string(),
    })


    /**
     * formik instance for find ticket form
     */
    const findTicketFormik = useFormik({
        initialValues: findTicketInitialValues,
        validationSchema: findTicketValidationSchema,
        onSubmit: async (values) => {
            try {
                setLoading(true);

                const payload = {
                    name: values.userName,
                    email: values.email,
                    phone: values.phone,
                    event_name: values.eventName,
                    booking_number: values.orderId
                }

                const response = await dispatch(findTickets(payload)).unwrap();

                if (response.code === 200) {
                    successToastTopRight('Tickets details have been sent to your email.');
                }

                setOpenGuestPopup(false);
                findTicketFormik.resetForm();
            } catch (error) {
                setOpenGuestPopup(true);
                switch (error.code) {
                    case 400:
                        errorToast(error.message || 'Invalid details provided. Please check and try again.');
                        break;
                    case 404:
                        errorToast(error.message || 'No tickets found matching the provided details.');
                        break;
                    default:
                        errorToast('Failed to find the ticket. Please try again later.');
                        break;
                }
            } finally {
                setLoading(false);
            }
        }
    })


    /**
     * @returns {Object} Return all necessary states and functions from this hook
     */
    return {
        enquiryTypes,
        formik,
        allCities,
        loading,
        openGuestPopup,
        setOpenGuestPopup,
        findTicketFormik
    }
}